/**
 * BadgesField component for displaying read-only array values as badges
 * Used for displaying array data that cannot be edited
 */

import { Badge } from "@/components/ui/badge";
import type { FieldConfig } from "../types";

interface BadgesFieldProps<T> {
  field: FieldConfig<T>;
  value: string[];
  onChange?: (value: string[]) => void;
  onBlur?: () => void;
  disabled?: boolean;
  id: string;
  placeholder?: string;
  "aria-describedby"?: string;
}

export function BadgesField<T>({
  field,
  value,
  id,
  placeholder = "No items configured",
  "aria-describedby": ariaDescribedBy,
}: BadgesFieldProps<T>) {
  // This is a read-only field, so we don't use onChange or onBlur
  
  if (!value || value.length === 0) {
    return (
      <div 
        id={id}
        className="text-sm text-muted-foreground"
        aria-describedby={ariaDescribedBy}
      >
        {placeholder || field.placeholder || "No items configured"}
      </div>
    );
  }

  return (
    <div 
      id={id}
      className="flex flex-wrap gap-2"
      aria-describedby={ariaDescribedBy}
    >
      {value.map((item) => (
        <Badge key={item} variant="secondary">
          {item}
        </Badge>
      ))}
    </div>
  );
}